import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, User, LogOut } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useClientAuth } from '../context/ClientAuthContext';
import { translations, companyInfo } from '../data/mockData';
import ExchangeRateBar from './ExchangeRateBar';
import { useSiteLayout } from '../context/SiteLayoutContext';
import { SiteBanner } from './SiteBanner';
import { GOLD, NAVY, CREAM, alpha } from '../theme/palette';

// Belmond Cathedral theme tokens
const SERIF = { fontFamily: '"Fraunces", serif' };

const FlagButton = ({ code, active, onClick, label }) => {
  const flags = {
    pt: (<svg viewBox="0 0 640 480" className="w-5 h-3.5 rounded-sm shadow-sm"><g strokeWidth="1pt"><path fill="#229e45" d="M0 0h640v480H0z"/><path fill="#f8e509" d="M321.4 36.2l301.5 195.7L321.4 435 16.9 232.4z"/><circle fill="#2b49a3" cx="321.4" cy="235.3" r="113.3"/></g></svg>),
    en: (<svg viewBox="0 0 640 480" className="w-5 h-3.5 rounded-sm shadow-sm"><g strokeWidth="1pt"><path fill="#bd3d44" d="M0 0h640v480H0"/><path stroke="#fff" strokeWidth="37" d="M0 55.3h640M0 129h640M0 203h640M0 277h640M0 351h640M0 425h640"/><path fill="#192f5d" d="M0 0h364.8v258.5H0"/></g></svg>),
    es: (<svg viewBox="0 0 640 480" className="w-5 h-3.5 rounded-sm shadow-sm"><path fill="#c60b1e" d="M0 0h640v480H0z"/><path fill="#ffc400" d="M0 120h640v240H0z"/></svg>),
  };
  return (
    <button onClick={() => onClick(code)} className={`flex items-center gap-1 px-1.5 py-1 rounded transition-all ${active ? 'ring-1 scale-105' : 'opacity-60 hover:opacity-100'}`} style={{ ...(active ? { boxShadow: `inset 0 0 0 1px ${GOLD}` } : {}) }} title={label} data-testid={`lang-${code}`}>
      {flags[code]}
    </button>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const { isAuthenticated: clientAuth, client, logout: clientLogout } = useClientAuth();
  const t = translations[language].nav;
  const navigate = useNavigate();
  const location = useLocation();

  // Scope the Belmond CSS overrides to public-site DOM only (so admin
  // panel at /admin/* keeps its old look). The flag is removed when the
  // Navbar unmounts (admin shell doesn't render Navbar).
  React.useEffect(() => {
    document.body.classList.add('public-mode');
    return () => document.body.classList.remove('public-mode');
  }, []);

  const whatsappNum = language === 'pt' ? companyInfo.whatsapp : companyInfo.whatsappUS;
  const phoneNum = language === 'pt' ? companyInfo.whatsapp : companyInfo.whatsappUS;

  const scrollToSection = (sectionId) => {
    setIsOpen(false);
    if (sectionId === 'top') {
      if (location.pathname !== '/') navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      const homeSections = ['about', 'testimonials', 'faq', 'contact'];
      if (homeSections.includes(sectionId)) navigate('/', { state: { scrollTo: sectionId } });
      else navigate(`/${sectionId}`);
    }
  };

  const linkCls = "text-[11px] tracking-[0.28em] uppercase font-medium transition-opacity opacity-75 hover:opacity-100";
  const mobileCls = "block w-full text-left py-2 text-sm font-medium tracking-wider uppercase opacity-80";
  const { layout } = useSiteLayout();
  const custom = (item) => (item?.[`label_${language}`] || '').trim();
  const defaultLabels = {
    home: t.home, pilgrimages: t.pilgrimages, gallery: language === 'pt' ? 'Galeria' : language === 'en' ? 'Gallery' : 'Galería',
    about: t.about, testimonials: t.testimonials, faq: t.faq, contact: t.contact,
  };
  const routes = { pilgrimages: '/pilgrimages', gallery: '/galeria' };
  const menuLinks = layout.menu.links.filter(l => l.visible && defaultLabels[l.key]);
  const btn = (key) => layout.menu.buttons.find(b => b.key === key) || { visible: true };
  const clientBtn = btn('client_area');
  const leadBtn = btn('lead_group');
  const renderLink = (l, mobile) => {
    const label = custom(l) || defaultLabels[l.key];
    const cls = mobile ? mobileCls : linkCls;
    const tid = `${mobile ? 'mobile-' : ''}navbar-link-${l.key}`;
    if (routes[l.key]) return <Link key={l.key} to={routes[l.key]} onClick={() => setIsOpen(false)} className={cls} data-testid={tid} data-font="menu">{label}</Link>;
    return <button key={l.key} type="button" onClick={() => scrollToSection(l.key === 'home' ? 'top' : l.key)} className={cls} data-testid={tid} data-font="menu">{label}</button>;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50" style={{ ...SERIF }}>
      <SiteBanner />
      {/* Top bar — slim, navy, gold accents. Iter 351 — top bar uses Inter
          sans-serif + larger text for legibility; the serif stays below. */}
      <div style={{ background: NAVY, color: CREAM, fontFamily: '"Inter", system-ui, sans-serif' }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between py-2 text-[12px] sm:text-xs tracking-wide">
          <div className="flex items-center gap-4 sm:gap-5">
            <a href={`tel:${phoneNum}`} className="hidden sm:flex items-center gap-1.5 hover:text-[color:var(--gold)] transition-colors font-medium" style={{ '--gold': GOLD }}>
              <Phone className="w-3.5 h-3.5" />
              <span>{phoneNum}</span>
            </a>
            <a href={`https://api.whatsapp.com/send?phone=${whatsappNum.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[color:var(--gold)] transition-colors font-medium" style={{ '--gold': GOLD }}>
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="hidden md:inline">WhatsApp</span>
            </a>
            <a href="https://www.instagram.com/demarre_tours?igsh=MWxvaHp1Z2ltc3gwNA==" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[color:var(--gold)] transition-colors font-medium" style={{ '--gold': GOLD }} data-testid="navbar-instagram-link">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              <span className="hidden md:inline">Instagram</span>
            </a>
          </div>
          <div className="flex items-center gap-1" data-testid="language-switcher">
            {language === 'pt' && <ExchangeRateBar variant="public" />}
            {language === 'pt' && <span className="opacity-30 mx-1">|</span>}
            <FlagButton code="pt" active={language === 'pt'} onClick={changeLanguage} label="Português" />
            <FlagButton code="en" active={language === 'en'} onClick={changeLanguage} label="English" />
            <FlagButton code="es" active={language === 'es'} onClick={changeLanguage} label="Español" />
          </div>
        </div>
      </div>

      {/* Main navigation — light cream, isolated logo, serif accents */}
      <div className="border-b" style={{ background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(12px)', borderColor: `${alpha(GOLD, '44')}` }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between py-3 lg:py-4 gap-6" style={{ fontFamily: '"Inter", sans-serif' }}>
            {/* Isolated logo — marcante, grande, sem texto */}
            <a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('top'); }} className="flex items-center cursor-pointer" aria-label="Demarré Tours — Início" data-testid="navbar-logo">
              <img src="/logo-demarre.png" alt="Demarré Tours" className="h-14 lg:h-20 w-auto object-contain transition-transform duration-500 hover:scale-[1.03]" style={{ filter: 'drop-shadow(0 4px 20px color-mix(in srgb, var(--site-gold) 22%, transparent))' }} />
            </a>

            {/* Desktop menu */}
            <div className="hidden lg:flex items-center gap-7" style={{ color: NAVY }}>
              {menuLinks.map(l => renderLink(l, false))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              {clientBtn.visible && <button
                onClick={() => navigate(clientAuth ? '/minhas-viagens' : '/login')}
                title={clientAuth ? undefined : (language === 'pt'
                  ? 'Entre para ver seu voucher, pagamentos, hotéis confirmados, itinerário detalhado e documentos da sua peregrinação.'
                  : language === 'en'
                    ? 'Sign in to view your voucher, payments, confirmed hotels, detailed itinerary and documents for your pilgrimage.'
                    : 'Inicie sesión para ver su voucher, pagos, hoteles confirmados, itinerario detallado y documentos de su peregrinación.')}
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase font-semibold px-4 py-2.5 rounded-full border-2 transition hover:bg-[color:var(--navy)] hover:text-[color:var(--gold)]"
                style={{ borderColor: GOLD, color: NAVY, '--navy': NAVY, '--gold': GOLD }}
                data-testid="client-area-btn"
              >
                <User className="w-3.5 h-3.5" />
                {clientAuth ? `${language === 'pt' ? 'Minha Conta' : language === 'en' ? 'My Account' : 'Mi Cuenta'} · ${client?.name?.split(' ')[0] || ''}` : (custom(clientBtn) || (language === 'pt' ? 'Entrar' : language === 'en' ? 'Sign In' : 'Ingresar'))}
              </button>}
              {leadBtn.visible && <Link to="/lead-a-group" className="text-[10px] tracking-[0.3em] uppercase font-semibold px-5 py-3 transition-opacity hover:opacity-90" style={{ background: NAVY, color: GOLD }} data-testid="navbar-lead-cta">
                {custom(leadBtn) || (language === 'pt' ? 'Organize um Grupo' : language === 'en' ? 'Lead a Group' : 'Organiza un Grupo')}
              </Link>}
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden" style={{ color: NAVY }} data-testid="mobile-menu-toggle">
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {isOpen && (
            <div className="lg:hidden pb-4 space-y-2" style={{ color: NAVY, fontFamily: '"Inter", sans-serif' }}>
              {menuLinks.map(l => renderLink(l, true))}
              {clientBtn.visible && <Link to={clientAuth ? '/minhas-viagens' : '/login'} onClick={() => setIsOpen(false)} className="block py-2 text-sm font-semibold tracking-wider uppercase" data-testid="mobile-client-area-btn">
                <User className="w-4 h-4 inline mr-2" />
                {clientAuth ? `${language === 'pt' ? 'Minha Conta' : language === 'en' ? 'My Account' : 'Mi Cuenta'} · ${client?.name?.split(' ')[0] || ''}` : (custom(clientBtn) || (language === 'pt' ? 'Entrar' : language === 'es' ? 'Iniciar Sesión' : 'Sign In'))}
              </Link>}
              {clientAuth && (
                <button
                  onClick={() => { clientLogout?.(); setIsOpen(false); }}
                  className="block py-1 text-xs tracking-wider uppercase text-red-600 hover:text-red-800 opacity-90"
                  data-testid="mobile-navbar-logout"
                >
                  <LogOut className="w-3 h-3 inline mr-1.5" />
                  {language === 'pt' ? 'Sair' : language === 'en' ? 'Log out' : 'Cerrar sesión'}
                </button>
              )}
              {leadBtn.visible && <Link to="/lead-a-group" onClick={() => setIsOpen(false)} className="block text-center text-xs tracking-[0.3em] uppercase font-semibold px-5 py-3 mt-3" style={{ background: NAVY, color: GOLD }} data-testid="mobile-navbar-lead-cta">
                {custom(leadBtn) || (language === 'pt' ? 'Organize um Grupo' : language === 'en' ? 'Lead a Group' : 'Organiza un Grupo')}
              </Link>}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
